import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { FilterSelection, Profile, ProfilesApiResponse } from '../../../tsm-app/models/tsm-app.model';
import {
	FormatDescriptor,
	FormatOption,
	isWorkbookOutput,
	ProfileOption,
	ReportAppDescriptor,
	ReportRangeSuggestion,
	ReportRangeSuggestionResponse,
	ReportWorkbookPreview,
	ReportWorkbookPreviewResponse,
	ReportRestoredView,
	ReportResult,
	ReportWorkbookStatus,
} from '../../models/report-app.model';
import { ReportAppApiService } from '../../services/report-app-api.service';
import { TsmAppFormService } from '../../../tsm-app/services/tsm-app-form.service';
import { TmsLabelService } from '../../../../language/services/tms-label.service';
import {
	isFormExpiredError,
	closeFormQuietly,
	restoreFormSnapshot,
	retryOnFormExpired,
	httpErrorMessage,
	resetForAppChange,
	surfaceRefusedEdit,
} from '../../../../shared/form-lifecycle';
import { TmsNotificationService } from '../../../../services/tms-notification.service';
import { ReportDownloadService } from '../../services/report-download.service';
import { expandWorkbookPreviewSheets } from '../../utils/workbook-preview';
import { toDraftSheets } from '../../../worksheet-app/utils/worksheet-draft-sheets';
import { carryProfileOptionsForward, computeActiveFilterCount, profilesToProfileOptions, projectFilterSelections } from '../../../filter-app/services/filter-panel-projection';
import { selectionsModified } from '../../../../shared/filters';
import {
	ApplyReportDescriptorUpdate,
	ApplyReportResult,
	BootstrapReportApp,
	ClearReportAppState,
	InitReportAppState,
	LoadReportAppProfiles,
	DownloadReportWorkbook,
	LoadReportRangeSuggestion,
	LoadReportWorkbookPreview,
	RebindReportForm,
	RefreshReportApp,
	RestoreReportAppSnapshot,
	RunReport,
	SetReportFormat,
	ToggleReportFilterPanel,
} from '../actions/report-app.actions';

/**
 * Report Apps — NGXS state slice owning the report-app shell lifecycle.
 * Mirrors {@code FilterAppState}'s descriptor lifecycle (bootstrap → refresh →
 * rebind) but swaps search/matrix-reload for {@link RunReport} (which stashes
 * the {@code {header,columns,rows}} result the renderer consumes).
 *
 * <p>The descriptor IS the contract — every show/hide / editability decision
 * happens server-side. This state caches the latest descriptor + the latest
 * run result + a thin UI scratchpad ({@code filterPanelOpen}).
 *
 * <p>Named {@code reportApp} (NOT {@code report}) to avoid colliding with the
 * unrelated Trovata {@code ReportsState}. See impl doc
 * {@code documentation/implementation/system/tms-report-app.md}.
 */

export class ReportAppStateModel {
	appName!: string | null;
	formId!: string | null;
	descriptor!: ReportAppDescriptor | null;
	result!: ReportResult | null;
	filterPanelOpen!: boolean;
	loading!: boolean;
	error!: string | null;
	// Why the last run of this form failed (the page shows it in place of the result); null when it ran or has not.
	// Kept apart from `error`, which also carries refused filter edits and profile-list failures.
	runError!: string | null;
	// After an empty run: the smallest date window with records (the page offers it as one action); null otherwise.
	rangeSuggestion!: ReportRangeSuggestion | null;
	// True while that window is being looked up, so the page shows it is searching instead of the no-data page first.
	rangeSuggestionLoading!: boolean;
	// An Excel-only run's workbook as the Workbooks grid's sheets (shown before the user downloads it); null otherwise.
	workbookPreview!: ReportWorkbookPreview | null;
	workbookPreviewLoading!: boolean;
	restoreInFlight!: boolean;
	profileCd!: string | null;
	// Profile baseline — filter selections captured when the active profile
	// loaded (BootstrapReportApp); `isProfileModified` compares live selections
	// against it to drive the profile-chip "modified" dot.
	baselineSelections!: Readonly<Record<string, FilterSelection>>;
	// Result view to re-apply after a snapshot restore (scroll + pages drawn); null otherwise.
	restoredView!: ReportRestoredView | null;
	// The form whose run produced `result` — the slice is shared by every report, so a restore
	// must not show another report's rows under this report's filters.
	resultFormId!: string | null;
	// The form whose RunReport is in flight — a second Run on it would start a second run (and file).
	runningFormId!: string | null;
	// The last Excel hand-over for this report; null until one is attempted.
	workbookStatus!: ReportWorkbookStatus | null;
}

/** What belongs to the report on screen — reset when another report opens (`resetForAppChange`). */
const REPORT_APP_OWNED_KEYS: ReadonlyArray<keyof ReportAppStateModel> = ['descriptor', 'result', 'resultFormId', 'baselineSelections'];

const DEFAULT_REPORT_APP_STATE: ReportAppStateModel = {
	appName: null,
	formId: null,
	descriptor: null,
	result: null,
	filterPanelOpen: false,
	loading: false,
	error: null,
	runError: null,
	rangeSuggestion: null,
	rangeSuggestionLoading: false,
	workbookPreview: null,
	workbookPreviewLoading: false,
	restoreInFlight: false,
	profileCd: null,
	baselineSelections: {},
	restoredView: null,
	resultFormId: null,
	runningFormId: null,
	workbookStatus: null,
};

@State<ReportAppStateModel>({
	name: 'reportApp',
	defaults: DEFAULT_REPORT_APP_STATE,
})
@Injectable()
export class ReportAppState {
	@Selector() static appName(state: ReportAppStateModel): string | null {
		return state.appName;
	}
	@Selector() static formId(state: ReportAppStateModel): string | null {
		return state.formId;
	}
	@Selector() static descriptor(state: ReportAppStateModel): ReportAppDescriptor | null {
		return state.descriptor;
	}
	@Selector() static result(state: ReportAppStateModel): ReportResult | null {
		return state.result;
	}
	@Selector() static restoredView(state: ReportAppStateModel): ReportRestoredView | null {
		return state.restoredView;
	}
	@Selector() static workbookStatus(state: ReportAppStateModel): ReportWorkbookStatus | null {
		return state.workbookStatus;
	}
	@Selector() static runError(state: ReportAppStateModel): string | null {
		return state.runError;
	}
	@Selector() static rangeSuggestion(state: ReportAppStateModel): ReportRangeSuggestion | null {
		return state.rangeSuggestion;
	}
	@Selector() static rangeSuggestionLoading(state: ReportAppStateModel): boolean {
		return state.rangeSuggestionLoading;
	}
	@Selector() static workbookPreview(state: ReportAppStateModel): ReportWorkbookPreview | null {
		return state.workbookPreview;
	}
	@Selector() static workbookPreviewLoading(state: ReportAppStateModel): boolean {
		return state.workbookPreviewLoading;
	}

	@Selector() static filterPanelOpen(state: ReportAppStateModel): boolean {
		return state.filterPanelOpen;
	}
	@Selector() static loading(state: ReportAppStateModel): boolean {
		return state.loading;
	}
	@Selector() static error(state: ReportAppStateModel): string | null {
		return state.error;
	}
	@Selector() static restoreInFlight(state: ReportAppStateModel): boolean {
		return state.restoreInFlight;
	}
	@Selector() static profileCd(state: ReportAppStateModel): string | null {
		return state.profileCd;
	}

	/**
	 * Count of active filters — value-set (SSoT): routes through the same
	 * projection + predicate the chips use, so the funnel badge == chip count
	 * (empty rows don't count; range pairs fold to one). Matches FilterAppState.
	 */
	@Selector() static activeFilterCount(state: ReportAppStateModel | undefined): number {
		const descriptor: ReportAppDescriptor | null = state?.descriptor ?? null;
		if (!descriptor) return 0;
		return computeActiveFilterCount(projectFilterSelections(Object.values(descriptor.fields)));
	}

	/** The loaded profile's saved selections — the baseline the "modified" comparisons run against. */
	@Selector() static baselineSelections(state: ReportAppStateModel): Readonly<Record<string, FilterSelection>> {
		return state.baselineSelections ?? {};
	}
	/** True when live filter selections differ from the loaded profile's baseline. */
	@Selector() static isProfileModified(state: ReportAppStateModel): boolean {
		const current: Readonly<Record<string, FilterSelection>> =
			projectFilterSelections(Object.values(state.descriptor?.fields ?? {}));
		return selectionsModified(current, state.baselineSelections ?? {});
	}

	private rangeLookupSeq: number = 0;
	private previewSeq: number = 0;
	/** The form whose workbook is being handed over: one workbook at a time per form (a second click gets no second file). */
	private handingOverFormId: string | null = null;

	constructor(
		private readonly api: ReportAppApiService,
		private readonly notify: TmsNotificationService,
		private readonly labels: TmsLabelService,
		private readonly formService: TsmAppFormService,
		private readonly downloads: ReportDownloadService
	) {}

	// -- Init / clear --

	@Action(InitReportAppState)
	initReportAppState(ctx: StateContext<ReportAppStateModel>, action: InitReportAppState): void {
		// Another report is opening: nothing of the one in the store may stay on screen (body, AI chip, breadcrumb).
		// Re-entering the same report keeps them: RebindReportForm re-runs only another form's result.
		ctx.patchState({
			...resetForAppChange(ctx.getState().appName, action.appName, DEFAULT_REPORT_APP_STATE, REPORT_APP_OWNED_KEYS),
			appName: action.appName,
			profileCd: action.profileCd ?? null,
			error: null,
			runError: null,
			rangeSuggestion: null,
			rangeSuggestionLoading: false,
			workbookPreview: null,
			workbookPreviewLoading: false,
			restoredView: null,
			// Release (null — do NOT close) any prior app's formId so the upcoming
			// BootstrapReportApp doesn't evict a form Path B has snapshotted for
			// return-nav. Init runs only on cross-app nav / first mount; same-app
			// re-bootstrap (profile switch / Clear) keeps formId and closes the
			// replaced form. Mirrors the REF/Filter fix — prevents the false
			// "session expired" on report→report→back. See ref-app.state.ts.
			formId: null,
			// The run state and workbook status belonged to the previous report.
			runningFormId: null,
			workbookStatus: null,
		});
	}

	@Action(ClearReportAppState)
	clearReportAppState(ctx: StateContext<ReportAppStateModel>): void {
		ctx.setState(DEFAULT_REPORT_APP_STATE);
	}

	// -- Lifecycle handlers (FORM_EXPIRED detection + restore via shared SSoT) --

	@Action(BootstrapReportApp)
	async bootstrapReportApp(
		ctx: StateContext<ReportAppStateModel>,
		action: BootstrapReportApp,
	): Promise<void> {
		// A bootstrap mints a new form: an earlier run in flight belongs to the old one, so it no longer
		// holds this page's in-flight state (its late finish must not clear the new form's loading).
		ctx.patchState({
			loading: true,
			error: null,
			runError: null,
			rangeSuggestion: null,
			rangeSuggestionLoading: false,
			workbookPreview: null,
			workbookPreviewLoading: false,
			runningFormId: null,
		});
		// Close any previous form before bootstrapping (profile switch, Clear).
		// Cross-app nav releases formId in InitReportAppState, so this only closes
		// same-app forms (no leak). Shared SSoT helper; non-fatal if it fails.
		await closeFormQuietly(this.formService, ctx.getState().formId, '[BootstrapReportApp]');
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const descriptor: ReportAppDescriptor = await firstValueFrom(
				this.api.bootstrap(action.appName, action.profileCd, idempotencyKey),
			);
			ctx.patchState({
				appName: action.appName,
				profileCd: action.profileCd ?? null,
				// Snapshot the saved-profile baseline for the "modified" dot. Reset on
				// every (re-)bootstrap — profile switch AND the Reset button (which
				// re-bootstraps), so after Reset the dot clears.
				baselineSelections: projectFilterSelections(Object.values(descriptor.fields ?? {})),
				// A bootstrap is a fresh start (initial load OR a Clear re-bootstrap):
				// drop any previously-rendered report so the displayed output is
				// cleared. Mirrors legacy refreshReportResults() which re-defaults the
				// form AND reverts the listing to "No Results to Display".
				result: null,
				resultFormId: null,
				workbookStatus: null,
			});
			// Awaited (was a bare dispatch): the applier is what patches formId, and the
			// auto-run below throws without one. NGXS dispatch happens to be eager for a
			// synchronous handler, but relying on that is a hidden coupling.
			await firstValueFrom(ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor)));
			ctx.dispatch(new LoadReportAppProfiles(action.appName));
			// Opening or switching a saved view RUNS its query (§18) — the report no
			// longer waits for a Search click. Dispatched here rather than from the page
			// so every "the view changed" path inherits it (launch, both profile-switch
			// paths, the panel's Reset), and the callers that must NOT run pass
			// autoRun:false. Every format runs: a run only produces the view (rows, or the
			// Excel table), and a file is handed over only by Download.
			if (action.autoRun) {
				// AWAITED, inside the try, so `loading` stays true for the whole open:
				// this method's finally clears it, and a fire-and-forget dispatch left
				// the body rendering "Run the report to see results." for the entire
				// engine pass — the very message this change exists to remove.
				try {
					await firstValueFrom(ctx.dispatch(new RunReport()));
				} catch (err: unknown) {
					// Deliberately not rethrown: RunReport already patched `error` into
					// this same slice, and failing the BOOTSTRAP would make the Path-B
					// restore hook read "no form" and answer with a "reopen fresh" toast.
					console.warn('[BootstrapReportApp] auto-run failed (error already surfaced)', err);
				}
			}
		} catch (err: unknown) {
			ctx.patchState({ error: httpErrorMessage(err) });
			throw err;
		} finally {
			ctx.patchState({ loading: false });
		}
	}

	@Action(LoadReportAppProfiles)
	async loadReportAppProfiles(
		ctx: StateContext<ReportAppStateModel>,
		action: LoadReportAppProfiles,
	): Promise<void> {
		try {
			const res: ProfilesApiResponse = await firstValueFrom(this.api.getProfiles(action.appName));
			const status: string = (res?.status ?? '').toLowerCase();
			if (!res || status !== 'success' || !Array.isArray(res.profiles)) {
				// 200-wrapped server failure — surface it (a silently-empty
				// dropdown reads as "no saved views" and feeds Update's
				// row-scope fallback).
				ctx.patchState({ error: 'Failed to load profiles.' });
				return;
			}
			const state: ReportAppStateModel = ctx.getState();
			const descriptor: ReportAppDescriptor | null = state.descriptor;
			if (!descriptor) return;
			// Shared mapper — capability flags ride along for the Manage dialog
			// (one field list for both filter AND report loaders).
			const available: ProfileOption[] = profilesToProfileOptions(res.profiles);
			ctx.patchState({
				descriptor: {
					...descriptor,
					profile: { ...descriptor.profile, available },
				},
			});
		} catch (err: unknown) {
			ctx.patchState({ error: httpErrorMessage(err) });
		}
	}

	@Action(RefreshReportApp)
	async refreshReportApp(
		ctx: StateContext<ReportAppStateModel>,
		action: RefreshReportApp,
	): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('RefreshReportApp dispatched with no formId — BootstrapReportApp must run first');
		}
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const descriptor: ReportAppDescriptor = await firstValueFrom(
				this.api.refresh(state.formId, action.changedFields, idempotencyKey),
			);
			ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor));
		} catch (err: unknown) {
			// A 410 on the REPLAY is surfaced too — never recover twice.
			if (!isFormExpiredError(err) || action.replay) {
				const message: string = httpErrorMessage(err);
				ctx.patchState({ error: message });
				surfaceRefusedEdit(this.notify, action.changedFields, message);
				throw err;
			}
			const appName: string | null = state.appName;
			if (!appName) {
				throw new Error('FORM_EXPIRED retry requires state.appName; missing');
			}
			await retryOnFormExpired(err, {
				reBootstrap: (): Promise<void> => this.reopenExpiredForm(ctx, appName, state),
				// autoRun:false — `replay` below re-runs the original action, so letting the
				// re-bootstrap auto-run too would run the report twice.
				replay: (): Promise<void> => firstValueFrom(ctx.dispatch(new RefreshReportApp(action.changedFields, true))),
			});
		}
	}

	@Action(DownloadReportWorkbook)
	async downloadReportWorkbook(ctx: StateContext<ReportAppStateModel>): Promise<void> {
		const appName: string | null = ctx.getState().appName;
		if (!appName) {
			return;
		}
		const state: ReportAppStateModel = ctx.getState();
		if (state.formId && this.handingOverFormId === state.formId) {
			this.notify.info(this.labels.resolve('report_workbook_in_progress_msg', 'Your workbook is still being generated.'));
			return;
		}
		this.handingOverFormId = state.formId;
		try {
			await this.handOverWorkbook(ctx, appName, true);
		} catch (err: unknown) {
			// The workbook waits on the page for the user, so its form can expire before Download (server restart,
			// session timeout): reopen the form, run it again and hand over that workbook — once; a second expiry is
			// reported by the export itself.
			await retryOnFormExpired(err, {
				reBootstrap: (): Promise<void> => this.reopenExpiredForm(ctx, appName, state),
				replay: async (): Promise<void> => {
					const reopenedFormId: string | null = ctx.getState().formId;
					await firstValueFrom(ctx.dispatch(new RunReport(true)));
					// The re-run shows the workbook again; hand it over — only on the form the user is still on, and only when
					// the run produced one (a failed run already said why).
					const phase: string | undefined = ctx.getState().workbookStatus?.phase;
					if (ctx.getState().formId === reopenedFormId && (phase === 'ready' || phase === 'no-records')) {
						await this.handOverWorkbook(ctx, appName);
					}
				},
			});
		} finally {
			this.handingOverFormId = null;
		}
	}

	@Action(LoadReportWorkbookPreview)
	async loadReportWorkbookPreview(ctx: StateContext<ReportAppStateModel>, action: LoadReportWorkbookPreview): Promise<void> {
		const appName: string | null = ctx.getState().appName;
		if (!appName) {
			return;
		}
		// Only the latest fetch may land: an earlier one finishing late belongs to a replaced run.
		const request: number = ++this.previewSeq;
		ctx.patchState({ workbookPreviewLoading: true });
		let preview: ReportWorkbookPreview | null = null;
		try {
			const response: ReportWorkbookPreviewResponse = await firstValueFrom(this.api.workbookPreview(appName, action.formId));
			if (response.sheets.length > 0) {
				preview = { sheets: toDraftSheets(expandWorkbookPreviewSheets(response.sheets)), truncated: response.truncated ?? [] };
			}
		} catch (err: unknown) {
			// The preview is a view of the file, not the file: without it the card still offers the download.
			console.warn('[LoadReportWorkbookPreview] no preview', err);
		}
		if (request !== this.previewSeq || !ctx.getState().workbookPreviewLoading || ctx.getState().formId !== action.formId) {
			return;
		}
		ctx.patchState({ workbookPreviewLoading: false, workbookPreview: preview });
	}

	@Action(LoadReportRangeSuggestion)
	async loadReportRangeSuggestion(ctx: StateContext<ReportAppStateModel>, action: LoadReportRangeSuggestion): Promise<void> {
		const appName: string | null = ctx.getState().appName;
		if (!appName) {
			return;
		}
		// Only the latest lookup may end the searching state: an earlier one finishing late belongs to a replaced result.
		const lookup: number = ++this.rangeLookupSeq;
		ctx.patchState({ rangeSuggestionLoading: true });
		let suggestion: ReportRangeSuggestion | null = null;
		try {
			const response: ReportRangeSuggestionResponse = await firstValueFrom(this.api.rangeSuggestion(appName, action.formId));
			suggestion = response.suggestion ?? null;
		} catch (err: unknown) {
			// The suggestion is an offer, not the report: without it the page shows the run's own no-data page.
			console.warn('[LoadReportRangeSuggestion] no suggestion', err);
		}
		if (lookup !== this.rangeLookupSeq || !ctx.getState().rangeSuggestionLoading) {
			return;
		}
		// Only for the form still on screen, and only while no newer run has replaced its empty result.
		const current: ReportAppStateModel = ctx.getState();
		const stillEmpty: boolean = current.formId === action.formId && !!current.result?.noRecords;
		ctx.patchState({ rangeSuggestionLoading: false, rangeSuggestion: stillEmpty ? suggestion : null });
	}

	@Action(RunReport)
	async runReport(ctx: StateContext<ReportAppStateModel>, action: RunReport): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('RunReport dispatched with no formId — BootstrapReportApp must run first');
		}
		const appName: string | null = state.appName;
		if (!appName) {
			throw new Error('RunReport dispatched with no appName — BootstrapReportApp must run first');
		}
		// A new run replaces the result, so a restored scroll position no longer applies.
		ctx.patchState({
			loading: true,
			runError: null,
			rangeSuggestion: null,
			rangeSuggestionLoading: false,
			workbookPreview: null,
			workbookPreviewLoading: false,
			runningFormId: state.formId,
			restoredView: null,
		});
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const result: ReportResult = await firstValueFrom(
				this.api.runReport(state.appName, state.formId, undefined, idempotencyKey),
			);
			// The user moved to another form (profile switch, another report) while this ran: its
			// rows and file belong to a page that is gone, so neither is shown or handed over.
			if (ctx.getState().formId !== state.formId) {
				return;
			}
			ctx.dispatch(new ApplyReportResult(result));
			// Nothing in the period: ask (without waiting) for a window that has records — the same offer in every format.
			if (result.noRecords) {
				ctx.dispatch(new LoadReportRangeSuggestion(state.formId));
			}
			// The run's VIEW in the picked format: Screen draws the rows stashed above; Excel shows the run's workbook. This
			// lives HERE so every caller gets it — the magnifier, the toolbar's Search, the panel's Apply, the range offer,
			// the open/switch/return auto-run and the AI's search. No run hands over a file; Download does.
			this.showWorkbookIfExcel(ctx);
		} catch (err: unknown) {
			// A run whose form is gone (the user moved on) must not report into the page now showing,
			// nor re-bootstrap it — that would undo the user's switch.
			if (ctx.getState().formId !== state.formId) {
				console.warn('[RunReport] a run for a form that is no longer shown failed; ignored', err);
				return;
			}
			// Form-expired (410) recovery — the cached server processor for this
			// formId is gone (server cycle / session timeout while the client still
			// holds a stale formId, e.g. after a hard refresh). Re-bootstrap to mint
			// a fresh processor + formId, then re-run (shared SSoT retry). appName is
			// guaranteed non-null by the guard above. A 410 on the REPLAY is surfaced — never recover twice.
			if (!isFormExpiredError(err) || action.replay) {
				const message: string = httpErrorMessage(err);
				ctx.patchState({
					error: message,
					runError: message,
					// A workbook report (download or preview) says the run failed, with the server's reason,
					// instead of falling back to its ready card.
					workbookStatus: isWorkbookOutput(state.descriptor)
						? { phase: 'run-failed', fileName: null, at: null, noRecords: false, message }
						: ctx.getState().workbookStatus,
				});
				throw err;
			}
			await retryOnFormExpired(err, {
				reBootstrap: (): Promise<void> => this.reopenExpiredForm(ctx, appName, state),
				// autoRun:false — `replay` below re-runs the original action, so letting the
				// re-bootstrap auto-run too would run the report twice.
				replay: (): Promise<void> => firstValueFrom(ctx.dispatch(new RunReport(true))),
			});
		} finally {
			// Only the form's own run ends its in-flight state; a stale run must not end another's.
			if (ctx.getState().runningFormId === state.formId) {
				ctx.patchState({ loading: false, runningFormId: null });
			}
		}
	}

	/**
	 * The Excel view of the form's last run: its workbook (loaded for the page's grid), or the shared empty state when the
	 * run found nothing. Screen needs nothing here — it draws the run's stashed rows. Every run and every format switch
	 * ends here, so both formats render the same run.
	 */
	private showWorkbookIfExcel(ctx: StateContext<ReportAppStateModel>): void {
		const state: ReportAppStateModel = ctx.getState();
		if (!isWorkbookOutput(state.descriptor) || !state.formId || !state.result || state.resultFormId !== state.formId) {
			return;
		}
		// The last run failed: the result on hand is an older run's, so Excel says why (as Screen does), never shows that.
		if (state.runError) {
			ctx.patchState({ workbookStatus: { phase: 'run-failed', fileName: null, at: null, noRecords: false, message: state.runError } });
			return;
		}
		const noRecords: boolean = state.result.noRecords === true;
		ctx.patchState({ workbookStatus: { phase: noRecords ? 'no-records' : 'ready', fileName: null, at: null, noRecords, message: null } });
		// A preview in the store is this run's (every run and bootstrap clears it): a switch back to Excel reuses it rather
		// than having the server parse the workbook again.
		if (!noRecords && !state.workbookPreview) {
			ctx.dispatch(new LoadReportWorkbookPreview(state.formId));
		}
	}

	/**
	 * Download: hand over the workbook the form's last run made (no new run). A run that found nothing hands over the
	 * headings-only file — legacy's file — only because the user asked for it. Failure is reported and swallowed: the run
	 * itself succeeded and stays on the page.
	 */
	private async handOverWorkbook(ctx: StateContext<ReportAppStateModel>, appName: string, passExpiry: boolean = false): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!isWorkbookOutput(state.descriptor) || !state.formId) {
			return;
		}
		const title: string = state.descriptor?.title || appName;
		const noRecords: boolean = state.result?.noRecords === true;
		try {
			const blob: Blob = await firstValueFrom(this.downloads.fetchFile(appName, state.formId, 'XLSX'));
			// The user moved to another form while the file was fetched: it is not theirs to receive now.
			if (ctx.getState().formId !== state.formId) {
				return;
			}
			const fileName: string = this.downloads.exportFilename(appName, state.result?.referenceId, 'xlsx');
			this.downloads.triggerDownload(blob, fileName);
			ctx.patchState({ workbookStatus: { phase: 'downloaded', fileName, at: Date.now(), noRecords, message: null } });
			if (noRecords) {
				this.notify.warning(
					`${title}: ${this.labels.resolve('report_workbook_no_records_msg', 'no records match your filters. The workbook has headings only.')}`,
					{ app: appName },
				);
			} else {
				this.notify.success(`${title} ${this.labels.resolve('report_workbook_downloaded_msg', 'workbook downloaded')}`, { app: appName });
			}
		} catch (err: unknown) {
			if (passExpiry && isFormExpiredError(err)) {
				throw err;
			}
			console.warn('[DownloadReportWorkbook] workbook download failed', err);
			if (ctx.getState().formId !== state.formId) {
				return;
			}
			ctx.patchState({ workbookStatus: { phase: 'failed', fileName: null, at: null, noRecords, message: null } });
			this.notify.error(this.labels.resolve('excel_export_failed_msg', 'Excel export failed.'));
		}
	}

	@Action(RebindReportForm)
	async rebindReportForm(
		ctx: StateContext<ReportAppStateModel>,
		action: RebindReportForm,
	): Promise<void> {
		const idempotencyKey: string = crypto.randomUUID();
		const descriptor: ReportAppDescriptor = await firstValueFrom(
			this.api.rebind(action.formId, idempotencyKey),
		);
		ctx.patchState({ profileCd: action.profileCd });
		await firstValueFrom(ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor)));
		// The result on screen belongs to whichever report ran last (e.g. this report → a record →
		// another report → back here). Re-run this form from the filters the server still holds; a run
		// only produces the view, in every format.
		const state: ReportAppStateModel = ctx.getState();
		if (state.resultFormId !== action.formId) {
			// Never show another report's rows under this report — cleared even when it cannot re-run.
			ctx.patchState({ result: null, resultFormId: null, workbookStatus: null });
		}
		if (state.resultFormId !== action.formId) {
			try {
				await firstValueFrom(ctx.dispatch(new RunReport()));
			} catch {
				// The rebind succeeded — keep this form. RunReport has already put its error in state
				// for the page to show; rethrowing would read as a failed rebind and reopen fresh.
			}
		}
	}

	/**
	 * Reopen a form the server no longer holds (410) as the same view: a new form from the active profile, keeping the
	 * format picked on the page — the recovery continues the user's work, it is not a Reset. Shared by every recovery.
	 */
	private async reopenExpiredForm(ctx: StateContext<ReportAppStateModel>, appName: string, expired: ReportAppStateModel): Promise<void> {
		await firstValueFrom(ctx.dispatch(new BootstrapReportApp(appName, expired.profileCd ?? undefined, false)));
		const picked: string | undefined = expired.descriptor?.format?.current;
		const reopened: ReportAppDescriptor | null = ctx.getState().descriptor;
		if (
			picked &&
			reopened?.format &&
			reopened.format.current !== picked &&
			reopened.format.available.some((option: FormatOption): boolean => option.id === picked)
		) {
			ctx.dispatch(new SetReportFormat(picked));
		}
	}

	// -- Internal appliers --

	@Action(ApplyReportDescriptorUpdate)
	applyReportDescriptorUpdate(
		ctx: StateContext<ReportAppStateModel>,
		action: ApplyReportDescriptorUpdate,
	): void {
		const state: ReportAppStateModel = ctx.getState();
		// Server descriptors carry an EMPTY profile.available — keep the loaded
		// saved-views list across same-app refresh/rebind (it was wiped on every
		// filter-chip change otherwise).
		const withProfiles: ReportAppDescriptor = carryProfileOptionsForward(state.descriptor, action.descriptor);
		const nextFormId: string = withProfiles.formId || state.formId || '';
		// The format picked on the page is an unsaved edit of THIS form, like a filter value: the server's
		// re-projection of the same form (refresh, rebind) echoes the profile's format and must not undo it. A new
		// form (bootstrap: profile switch, Reset, Clear) starts from its profile's format.
		const picked: string | undefined = state.descriptor?.format?.current;
		const keepPicked: boolean =
			!!state.formId &&
			nextFormId === state.formId &&
			!!withProfiles.format &&
			withProfiles.format.available.some((option: FormatOption): boolean => option.id === picked);
		const descriptor: ReportAppDescriptor = picked && keepPicked ? { ...withProfiles, format: { ...withProfiles.format, current: picked } } : withProfiles;
		ctx.patchState({
			descriptor,
			formId: nextFormId || state.formId,
		});
	}

	@Action(ApplyReportResult)
	applyReportResult(ctx: StateContext<ReportAppStateModel>, action: ApplyReportResult): void {
		const formId: string | null = action.result.formId || ctx.getState().formId;
		ctx.patchState({
			result: action.result,
			formId,
			resultFormId: formId,
		});
	}

	@Action(SetReportFormat)
	setReportFormat(ctx: StateContext<ReportAppStateModel>, action: SetReportFormat): void {
		const descriptor: ReportAppDescriptor | null = ctx.getState().descriptor;
		if (!descriptor) return;
		ctx.patchState({
			descriptor: {
				...descriptor,
				format: { ...descriptor.format, current: action.format },
			},
			workbookStatus: null,
		});
		// The format picks how the same run is drawn: Excel shows its workbook (Screen draws the stashed rows). While a run
		// is in flight the result on hand is the previous run's, so nothing is loaded — the run's own end loads its workbook.
		const after: ReportAppStateModel = ctx.getState();
		if (after.runningFormId !== after.formId) {
			this.showWorkbookIfExcel(ctx);
		}
	}

	@Action(ToggleReportFilterPanel)
	toggleReportFilterPanel(ctx: StateContext<ReportAppStateModel>, action: ToggleReportFilterPanel): void {
		ctx.patchState({ filterPanelOpen: action.open });
	}

	// -- Path B mini-orchestrator --

	@Action(RestoreReportAppSnapshot)
	async restoreReportAppSnapshot(
		ctx: StateContext<ReportAppStateModel>,
		{ snapshot }: RestoreReportAppSnapshot,
	): Promise<void> {
		// Path B form recovery via the shared SSoT orchestrator.
		await restoreFormSnapshot({
			hasFormId: !!snapshot.formId,
			isRestoreInFlight: () => ctx.getState().restoreInFlight,
			setRestoreInFlight: (v: boolean) => ctx.patchState({ restoreInFlight: v }),
			// Report is a read-only runner (no edit mode), so a rebind 410 never loses
			// user edits — reopen fresh silently, never the "unsaved changes gone" toast.
			editAtRisk: false,
			rebind: () => firstValueFrom(ctx.dispatch(new RebindReportForm(
				snapshot.formId!,
				snapshot.profileCd,
			))),
			bootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapReportApp(
				snapshot.appName,
				snapshot.profileCd ?? undefined,
			))),
			// Report has a single result surface (no coordinator); restore the UI
			// slices the server doesn't own — the filter panel and where the result
			// view was scrolled (the page re-applies it once the result renders).
			onRestored: (): void => {
				ctx.patchState({
					filterPanelOpen: snapshot.filterPanelOpen,
					restoredView: { scrollTop: snapshot.scrollTop, renderedPages: snapshot.renderedPages ?? 0 },
				});
				// The format picked before leaving comes back with the rest of the view, when the form still offers it.
				const format: FormatDescriptor | undefined = ctx.getState().descriptor?.format;
				if (format && format.current !== snapshot.format && format.available.some((option: FormatOption): boolean => option.id === snapshot.format)) {
					ctx.dispatch(new SetReportFormat(snapshot.format));
				}
			},
			notify: this.notify,
			labels: this.labels,
			logTag: '[RestoreReportAppSnapshot]',
		});
	}
}
